import type { RiftboundCard } from '../types';
import type { DeckDraft, DeckRune, DeckValidationIssue, DeckValidationZone, ValidationResult } from '../types/deck';
import { DECK_LIMITS, mainDeckCount, sideboardCount, runeCount } from '../types/deck';
import { getApiBaseUrl } from '../constants/environment';
import { riftAtlasPrintCode } from './cardPrintings';
import { cardOriginSet, isPromoSetCode } from './cardPrintVariant';
import { exportRuneLabel } from './runePrint';
import {
  championIdentityFromName,
  championIdentityMatchesLegend,
  isChampionUnitCard,
} from './championIdentity';
import { exceedsCopyLimit, isAtCopyLimit, maxCopiesForCardName } from './deckCopyLimits';
import { isBannedBattlefield, isBannedCard, isCardBanned } from './isBanned';
import { BAN_LIST_FORMAT } from '../constants/banList';
import { chosenLegendProblems } from '../../shared/chosenLegends.js';

function issue(zone: DeckValidationZone, message: string): DeckValidationIssue {
  return { zone, message };
}

/**
 * Full deck construction validator based on Riftbound Core Rules and Tournament Rules.
 *
 * Rules checked:
 * 1. Legend must be selected
 * 2. A Chosen Champion must be selected; it must be a Champion unit whose *identity*
 *    matches the Legend (same leading name as the legend title, e.g. "Darius" with "Darius, Hand of Noxus").
 * 3. Exactly 3 battlefields with unique names
 * 4. Exactly 12 runes total; all from the legend's domains
 * 5. Main deck exactly 40 cards total — the Chosen Champion counts as 1 of those 40 (placed in Champion Zone at game start)
 * 6. Max 3 copies of any named card across champion + main + sideboard (see deckCopyLimits for exceptions)
 * 7. All main deck + sideboard cards within the legend's domains (or colorless)
 * 8. Sideboard has at most 10 cards (0–10 inclusive)
 * 9. With Neeko, Blending In in the deck: exactly 3 chosen Legends, no two sharing a
 *    name with each other or with the starting Legend (`shared/chosenLegends.js`)
 */
export function validateDeck(deck: DeckDraft): ValidationResult {
  const issues: DeckValidationIssue[] = [];
  const warnings: string[] = [];

  const domains = deck.legend?.domain ?? [];

  // --- 1. Legend ---
  if (!deck.legend) {
    issues.push(issue('legend', 'A Legend is required.'));
  }
  for (const message of chosenLegendProblems(deck)) {
    issues.push(issue('chosenLegends', message));
  }

  // --- 2. Champion ---
  if (!deck.champion) {
    issues.push(issue('champion', 'A Chosen Champion is required.'));
  } else if (deck.legend) {
    if (!isChampionUnitCard(deck.champion)) {
      issues.push(
        issue(
          'champion',
          `"${deck.champion.name}" is not a Champion unit — it cannot be your Chosen Champion.`,
        ),
      );
    } else if (!championIdentityMatchesLegend(deck.champion, deck.legend)) {
      const want = championIdentityFromName(deck.legend.name);
      const got = championIdentityFromName(deck.champion.name);
      issues.push(
        issue(
          'champion',
          `Chosen Champion must match your Legend's identity (expected "${want}", "${deck.champion.name}" is "${got || '…'}").`,
        ),
      );
    }
  }

  // --- 3. Battlefields ---
  if (deck.battlefields.length !== DECK_LIMITS.BATTLEFIELD_COUNT) {
    issues.push(
      issue(
        'battlefield',
        `Exactly ${DECK_LIMITS.BATTLEFIELD_COUNT} Battlefields are required (have ${deck.battlefields.length}).`,
      ),
    );
  } else {
    const bfNames = deck.battlefields.map((b) => b.name.toLowerCase());
    const uniqueNames = new Set(bfNames);
    if (uniqueNames.size !== bfNames.length) {
      issues.push(issue('battlefield', 'Battlefields must have unique names.'));
    }
  }
  for (const bf of deck.battlefields) {
    if (isBannedBattlefield(bf.name)) {
      issues.push(issue('battlefield', `"${bf.name}" is banned in ${BAN_LIST_FORMAT}.`));
    }
  }

  // --- 4. Runes ---
  const totalRunes = runeCount(deck);
  if (totalRunes !== DECK_LIMITS.RUNE_COUNT) {
    issues.push(
      issue(
        'runes',
        `Exactly ${DECK_LIMITS.RUNE_COUNT} Runes are required (have ${totalRunes}).`,
      ),
    );
  }
  if (deck.legend && deck.runes.length > 0) {
    for (const rune of deck.runes) {
      if (!domains.includes(rune.domain)) {
        issues.push(
          issue(
            'runes',
            `Rune domain "${rune.domain}" is not in the Legend's domains (${domains.join(', ')}).`,
          ),
        );
      }
    }
  }

  // --- 5. Main deck size ---
  const deckCount = mainDeckCount(deck);
  if (deckCount !== DECK_LIMITS.MAIN_DECK_SIZE) {
    let mainSizeMsg = `Main Deck must have exactly ${DECK_LIMITS.MAIN_DECK_SIZE} cards (have ${deckCount}).`;
    if (deckCount > DECK_LIMITS.MAIN_DECK_SIZE) {
      const excess = deckCount - DECK_LIMITS.MAIN_DECK_SIZE;
      mainSizeMsg += ` Remove or move ${excess} card${excess !== 1 ? 's' : ''} to Considered.`;
    }
    issues.push(issue('mainDeck', mainSizeMsg));
  }

  // --- 6. Copy limits (champion + main deck + sideboard combined) ---
  const copyCounts = new Map<string, number>();
  if (deck.champion) {
    copyCounts.set(deck.champion.name, 1);
  }
  for (const card of deck.mainDeck) {
    copyCounts.set(card.name, (copyCounts.get(card.name) ?? 0) + card.qty);
  }
  for (const card of deck.sideboard) {
    copyCounts.set(card.name, (copyCounts.get(card.name) ?? 0) + card.qty);
  }
  for (const [name, count] of copyCounts) {
    if (exceedsCopyLimit(name, count)) {
      const max = maxCopiesForCardName(name);
      const inSide = (deck.sideboard.find((c) => c.name === name)?.qty ?? 0) > 0;
      issues.push(
        issue(
          inSide && !(deck.mainDeck.some((c) => c.name === name) || deck.champion?.name === name)
            ? 'sideboard'
            : 'mainDeck',
          `"${name}" exceeds the ${max}-copy limit (have ${count} across main deck and sideboard).`,
        ),
      );
    }
  }

  // --- 7. Domain legality + ban check for main deck + sideboard ---
  for (const card of deck.mainDeck) {
    if (isBannedCard(card.name)) {
      issues.push(issue('mainDeck', `"${card.name}" is banned in ${BAN_LIST_FORMAT}.`));
    }
  }
  for (const card of deck.sideboard) {
    if (isBannedCard(card.name)) {
      issues.push(issue('sideboard', `Sideboard card "${card.name}" is banned in ${BAN_LIST_FORMAT}.`));
    }
  }
  if (deck.legend) {
    for (const card of deck.mainDeck) {
      if (!isDomainLegal(card.domain ?? [], domains)) {
        issues.push(issue('mainDeck', `"${card.name}" is not legal for this Legend's domains.`));
      }
    }
    for (const card of deck.sideboard) {
      if (!isDomainLegal(card.domain ?? [], domains)) {
        issues.push(
          issue('sideboard', `Sideboard card "${card.name}" is not legal for this Legend's domains.`),
        );
      }
    }
  }

  // --- 8. Sideboard size ---
  const sbCount = sideboardCount(deck);
  if (sbCount > DECK_LIMITS.SIDEBOARD_MAX) {
    issues.push(
      issue(
        'sideboard',
        `Sideboard cannot exceed ${DECK_LIMITS.SIDEBOARD_MAX} cards (have ${sbCount}).`,
      ),
    );
  }

  // --- Warnings ---
  if (deck.title.trim().length === 0) {
    warnings.push('Add a deck title before publishing.');
  }
  if (deckCount > 0 && deckCount < DECK_LIMITS.MAIN_DECK_SIZE) {
    const remaining = DECK_LIMITS.MAIN_DECK_SIZE - deckCount;
    warnings.push(`${remaining} more card${remaining !== 1 ? 's' : ''} needed.`);
  }
  if (totalRunes > 0 && totalRunes < DECK_LIMITS.RUNE_COUNT) {
    warnings.push(`${DECK_LIMITS.RUNE_COUNT - totalRunes} more rune${DECK_LIMITS.RUNE_COUNT - totalRunes !== 1 ? 's' : ''} needed.`);
  }

  const errors = issues.map((i) => i.message);
  return { isValid: errors.length === 0, errors, warnings, issues };
}

/** Messages for a single zone (inline section coaching). */
export function validationMessagesForZone(
  result: ValidationResult,
  zone: DeckValidationZone,
): string[] {
  return (result.issues ?? []).filter((i) => i.zone === zone).map((i) => i.message);
}

/**
 * Count / missing-slot issues already restated by section titles (e.g. "Battlefields 0/3").
 * Keep those out of the inline subtitle; surface only specific construction problems.
 */
function isCountRestatementIssue(message: string): boolean {
  return (
    message === 'A Legend is required.' ||
    message === 'A Chosen Champion is required.' ||
    /Battlefields are required/.test(message) ||
    /Runes are required/.test(message) ||
    /Main Deck must have exactly/.test(message) ||
    /Sideboard cannot exceed/.test(message)
  );
}

function shortenInlineLegalityHint(message: string): string {
  if (message.includes('not a Champion unit')) return 'Not a Champion unit.';
  if (message.includes('must match your Legend')) return 'Must match Legend identity.';
  if (message === 'Battlefields must have unique names.') return 'Names must be unique.';
  if (/is banned/.test(message)) {
    const named = message.match(/^"([^"]+)" is banned/);
    if (named) return `"${named[1]}" is banned.`;
    const sideBanned = message.match(/^Sideboard card "([^"]+)" is banned/);
    if (sideBanned) return `"${sideBanned[1]}" is banned.`;
  }
  if (/not in the Legend's domains/.test(message)) {
    const domain = message.match(/^Rune domain "([^"]+)"/);
    return domain ? `Off-domain rune (${domain[1]}).` : 'Off-domain rune.';
  }
  if (/not legal for this Legend's domains/.test(message)) {
    const named = message.match(/"([^"]+)" is not legal/);
    if (named) return `"${named[1]}" is off-domain.`;
    const side = message.match(/Sideboard card "([^"]+)"/);
    if (side) return `"${side[1]}" is off-domain.`;
    return 'Off-domain card.';
  }
  if (/exceeds the \d+-copy limit/.test(message)) {
    const named = message.match(/^"([^"]+)" exceeds the (\d+)-copy limit/);
    if (named) return `"${named[1]}" over ${named[2]}-copy limit.`;
  }
  return message;
}

/** Compact zone hints for section UI — skips count restatements already in the label. */
export function inlineLegalityHintsForZone(
  result: ValidationResult,
  zone: DeckValidationZone,
): string[] {
  return validationMessagesForZone(result, zone)
    .filter((msg) => !isCountRestatementIssue(msg))
    .map(shortenInlineLegalityHint);
}

/** True when the zone has any construction issue (for heading tone even without a subtitle). */
export function zoneHasValidationIssue(
  result: ValidationResult,
  zone: DeckValidationZone,
): boolean {
  return validationMessagesForZone(result, zone).length > 0;
}

/**
 * A card is domain-legal when every domain it belongs to is within the
 * legend's domain identity. Colorless cards (empty domain) are always legal.
 */
function isDomainLegal(cardDomains: string[], legendDomains: string[]): boolean {
  if (cardDomains.length === 0) return true; // colorless
  if (legendDomains.length === 0) return true; // no legend selected yet
  return cardDomains.every((d) => legendDomains.includes(d));
}

/** Quick check: is a given card legally addable to the current deck? */
export function canAddCard(
  cardName: string,
  cardDomains: string[],
  legendDomains: string[],
  currentMainDeck: { name: string; qty: number }[],
  currentSideboard: { name: string; qty: number }[]
): { canAdd: boolean; reason?: string } {
  if (isCardBanned(cardName)) {
    return { canAdd: false, reason: `Banned in ${BAN_LIST_FORMAT}` };
  }
  if (!isDomainLegal(cardDomains, legendDomains)) {
    return { canAdd: false, reason: "Not legal for this Legend's domains" };
  }
  const existingMain = currentMainDeck.find((c) => c.name === cardName)?.qty ?? 0;
  const existingSide = currentSideboard.find((c) => c.name === cardName)?.qty ?? 0;
  const existing = existingMain + existingSide;
  if (isAtCopyLimit(cardName, existing)) {
    return { canAdd: false, reason: `Already at ${maxCopiesForCardName(cardName)}-copy limit` };
  }
  return { canAdd: true };
}

/**
 * Strip print-specific suffixes from a card name for export.
 * These suffixes identify a physical print variant, not a different card —
 * exporters and tournament tools should use the canonical card name.
 *
 * Strips: (Alternate Art), (Overnumbered), (Signed), (Signature), (Ultimate)
 */
export function exportCardName(name: string): string {
  return name
    .replace(/\s*\(Alternate Art\)\s*$/i, '')
    .replace(/\s*\(Overnumbered\)\s*$/i, '')
    .replace(/\s*\(Signed\)\s*$/i, '')
    .replace(/\s*\(Signature\)\s*$/i, '')
    .replace(/\s*\(Ultimate\)\s*$/i, '')
    .trim();
}

type DeckTextExportFormat = 'tcgarena' | 'riftatlas';

function deckSectionHeader(format: DeckTextExportFormat, label: string): string {
  return format === 'tcgarena' ? `# ${label}` : `${label}:`;
}

/** Rift Atlas titles use a comma (`Leona, Radiant Dawn`), not our dash. */
function riftAtlasCardName(name: string): string {
  return exportCardName(name).replace(/\s+[-\u2010-\u2015\u2212]\s+/g, ', ');
}

function exportNamedCard(
  card: { name: string; setCode?: string; number?: string; riftboundId?: string; originSet?: string },
  qty: number,
  format: DeckTextExportFormat,
): string {
  const name = format === 'riftatlas' ? riftAtlasCardName(card.name) : exportCardName(card.name);
  if (format !== 'riftatlas') return `${qty} ${name}`;
  const code = riftAtlasPrintCode(card);
  return code ? `${qty} ${name} [${code}]` : `${qty} ${name}`;
}

function exportDeckAsTextWithFormat(draft: DeckDraft, format: DeckTextExportFormat): string {
  const lines: string[] = [];

  if (draft.legend) {
    lines.push(deckSectionHeader(format, 'Legend'));
    lines.push(exportNamedCard(draft.legend, 1, format));
    lines.push('');
  }

  const chosenLegends = draft.chosenLegends ?? [];
  if (chosenLegends.length > 0) {
    // Rift Atlas only recognizes Neeko's picks under "Additional Legends".
    lines.push(deckSectionHeader(format, format === 'riftatlas' ? 'Additional Legends' : 'Chosen Legends'));
    for (const legend of chosenLegends) {
      lines.push(exportNamedCard(legend, 1, format));
    }
    lines.push('');
  }

  if (draft.champion) {
    lines.push(deckSectionHeader(format, 'Champion'));
    lines.push(exportNamedCard(draft.champion, 1, format));
    lines.push('');
  }

  if (draft.battlefields.length > 0) {
    lines.push(deckSectionHeader(format, 'Battlefields'));
    for (const bf of draft.battlefields) {
      lines.push(exportNamedCard(bf, 1, format));
    }
    lines.push('');
  }

  const exportedRunes = draft.runes.filter((rune) => rune.qty > 0);
  if (exportedRunes.length > 0) {
    lines.push(deckSectionHeader(format, 'Runes'));
    for (const rune of exportedRunes) {
      lines.push(`${rune.qty} ${exportRuneLabel(rune, format === 'riftatlas' ? 'riftatlas' : 'plain')}`);
    }
    lines.push('');
  }

  if (draft.mainDeck.length > 0) {
    lines.push(deckSectionHeader(format, 'Main Deck'));
    if (format === 'tcgarena') {
      const grouped = groupByType(draft.mainDeck);
      for (const [type, cards] of Object.entries(grouped)) {
        lines.push(`## ${type}`);
        for (const card of cards) {
          lines.push(exportNamedCard(card, card.qty, format));
        }
      }
    } else {
      for (const card of draft.mainDeck) {
        lines.push(exportNamedCard(card, card.qty, format));
      }
    }
    lines.push('');
  }

  if (draft.sideboard.length > 0) {
    lines.push(deckSectionHeader(format, 'Sideboard'));
    for (const card of draft.sideboard) {
      lines.push(exportNamedCard(card, card.qty, format));
    }
  }

  return lines.join('\n').trim();
}

/** Generate a plain-text export for TCG Arena (markdown-style sections, grouped by card type). */
export function exportDeckAsText(draft: DeckDraft): string {
  return exportDeckAsTextWithFormat(draft, 'tcgarena');
}

/** Generate a plain-text export for Rift Atlas (play.riftatlas.com). */
export function exportDeckAsRiftAtlasText(draft: DeckDraft): string {
  return exportDeckAsTextWithFormat(draft, 'riftatlas');
}

type PromoSlot = { cardId?: string; setCode?: string; riftboundId?: string; originSet?: string };

function promoLookupId(slot: PromoSlot | undefined): string | undefined {
  if (!slot || slot.originSet || !isPromoSetCode(slot.setCode)) return undefined;
  return slot.cardId || slot.riftboundId || undefined;
}

function stampOrigin<T extends PromoSlot>(slot: T, origins: Map<string, string>): T {
  const id = promoLookupId(slot);
  const origin = id ? origins.get(id) : undefined;
  return origin ? { ...slot, originSet: origin } : slot;
}

async function fetchPromoOrigins(ids: string[]): Promise<Map<string, string>> {
  const origins = new Map<string, string>();
  if (!ids.length) return origins;
  const url = `${getApiBaseUrl()}/api/card-print?ids=${ids.map(encodeURIComponent).join(',')}`;
  const response = await fetch(url);
  if (!response.ok) return origins;
  const data = (await response.json()) as { cards?: RiftboundCard[] };
  for (const card of data.cards ?? []) {
    const origin = cardOriginSet(card);
    if (!origin) continue;
    origins.set(card.id, origin);
    if (card.riftboundId) origins.set(card.riftboundId, origin);
  }
  return origins;
}

/**
 * Saved promo printings only store the catalog set (`OPP`). Rift Atlas wants
 * the parent product code (`VEN-R02B`). Fills `originSet` from the catalog
 * when the deck does not already have it.
 */
export async function withRiftAtlasPromoOrigins(
  draft: DeckDraft,
  lookup: (ids: string[]) => Promise<Map<string, string>> = fetchPromoOrigins,
): Promise<DeckDraft> {
  const ids = new Set<string>();
  const consider = (slot?: PromoSlot | null) => {
    const id = promoLookupId(slot ?? undefined);
    if (id) ids.add(id);
  };
  consider(draft.legend);
  (draft.chosenLegends ?? []).forEach(consider);
  consider(draft.champion);
  draft.battlefields.forEach(consider);
  draft.mainDeck.forEach(consider);
  draft.sideboard.forEach(consider);
  draft.considered.forEach(consider);
  for (const rune of draft.runes) consider(rune.print);
  if (ids.size === 0) return draft;

  const origins = await lookup([...ids]);
  const stampRune = (rune: DeckRune): DeckRune =>
    rune.print ? { ...rune, print: stampOrigin(rune.print, origins) } : rune;
  return {
    ...draft,
    legend: draft.legend ? stampOrigin(draft.legend, origins) : undefined,
    chosenLegends: (draft.chosenLegends ?? []).map((card) => stampOrigin(card, origins)),
    champion: draft.champion ? stampOrigin(draft.champion, origins) : undefined,
    battlefields: draft.battlefields.map((card) => stampOrigin(card, origins)),
    mainDeck: draft.mainDeck.map((card) => stampOrigin(card, origins)),
    sideboard: draft.sideboard.map((card) => stampOrigin(card, origins)),
    considered: draft.considered.map((card) => stampOrigin(card, origins)),
    runes: draft.runes.map(stampRune),
  };
}

function groupByType(
  cards: { cardType?: string; name: string; qty: number }[]
): Record<string, { name: string; qty: number }[]> {
  const order = ['Unit', 'Spell', 'Gear'];
  const result: Record<string, { name: string; qty: number }[]> = {};
  for (const card of cards) {
    const type = card.cardType || 'Other';
    if (!result[type]) result[type] = [];
    result[type].push({ name: card.name, qty: card.qty });
  }
  const sorted: Record<string, { name: string; qty: number }[]> = {};
  for (const t of order) {
    if (result[t]) sorted[t] = result[t];
  }
  for (const t of Object.keys(result)) {
    if (!sorted[t]) sorted[t] = result[t];
  }
  return sorted;
}
